/**
 * SpeakerButton: 스피커 토글 + 디바이스 선택 드롭다운.
 *
 * v1 원본: RenewalComponents/Buttons/SpeakerButton/SpeakerButtonPresenter.js
 * v1 CSS: .rtc_new3 .newHeader_btnItem > .newHeader_btn > .newHeader_subBtn
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { localActions } from '@/store/slices/localSlice';

interface SpeakerButtonProps {
  onChangeDevice?: (type: string, deviceId: string) => void;
}

const SpeakerButton: React.FC<SpeakerButtonProps> = ({ onChangeDevice }) => {
  const dispatch = useAppDispatch();
  const isMuted = useAppSelector((s) => s.local.speakerMuted);
  const audioOutputDevices = useAppSelector((s) => s.local.audioOutputDevices);
  const audioOutputDeviceId = useAppSelector((s) => s.local.audioOutputDeviceId);

  const [isListOpen, setIsListOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleMuteSpeaker = useCallback(() => {
    dispatch(localActions.setSpeakerMuted(!isMuted));
  }, [dispatch, isMuted]);

  // 외부 클릭 닫기
  useEffect(() => {
    if (!isListOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsListOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isListOpen]);

  // 키보드 단축키: Ctrl+Shift+S
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'S') {
        toggleMuteSpeaker();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleMuteSpeaker]);

  const handleSelectDevice = (deviceId: string) => {
    onChangeDevice?.('audiooutput', deviceId);
    setIsListOpen(false);
  };

  return (
    <div ref={containerRef} className={`newHeader_btnItem${isListOpen ? ' open' : ''}`}>
      <div className="newHeader_btn">
        <button
          type="button"
          className={`newHeader_subBtn${isMuted ? ' disabled' : ''}`}
          onClick={toggleMuteSpeaker}
        >
          <span className="newHeader_btnIcon icoSpk" />
        </button>
        <button
          type="button"
          className="newHeader_subBtn newHeader_subBtn--dropdown"
          onClick={() => setIsListOpen(!isListOpen)}
        >
          <p className="newHeader_btnText">스피커</p>
        </button>
      </div>
      {isListOpen && (
        <div className="viewerHeader__layer">
          <strong className="viewerHeader__layerTitle">스피커 선택</strong>
          <ul className="viewerHeader__layerList">
            {audioOutputDevices.map((device) => (
              <li key={device.deviceId}>
                <button
                  type="button"
                  className={`viewerHeader__layerListBtn${device.deviceId === audioOutputDeviceId ? ' selected' : ''}`}
                  onClick={() => handleSelectDevice(device.deviceId)}
                >
                  {device.label || `스피커 ${device.deviceId.slice(0, 5)}`}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SpeakerButton;
